import { debug } from 'console'

import type {
  HistoryLocationState,
  HistoryStackState
} from '@epilot/journey-logic-commons'
import { EMBEDDED_JOURNEY_MESSAGE_EVENT_TYPE } from '@epilot/journey-logic-commons'
import type { UncastObject } from '@epilot/journey-logic-commons/src/types/next'
import { dash } from 'radashi'
import { useEffect } from 'react'

import { dispatchUserEvent } from '../blocks-renderers'
import { prepareTrackingData } from '../blocks-renderers/utils/hooks/useHistoryStack/prepareTrackingData'
import { getIsTrackingDisabled } from '../blocks-renderers/utils/hooks/useHistoryStack/utils'
import { selectJourney } from '../store/selectors/config'
import { selectCurrentStep } from '../store/selectors/runtime'

import { useAppStoreSelector } from './useAppStoreSelector'
import { usePrevious } from './usePrevious'

type Props = {
  /** @deprecated it sucks, therefore being replaced by the new appStore */
  history: HistoryStackState
}

export const useAppEvents = ({ history }: Props) => {
  const currentStep = useAppStoreSelector(selectCurrentStep)
  const lastCurrentStep = usePrevious(currentStep)
  const journey = useAppStoreSelector(selectJourney)

  /**
   * Sends a view event to Datadog whenever the current Step changes,
   * indicating the user either landed on the Journey or navigated somewhere.
   *
   * Only the {@link currentStep.id} is observed and that is intentionally done to avoid
   * sending unnecessary events.
   *
   * TODO: Add support for the new logics engine (can't rely on history stack)
   */
  useEffect(() => {
    if (!journey?.id) return // mostly to cerce TS

    if (currentStep && currentStep?.id !== lastCurrentStep?.id) {
      const location = history.location

      if (getIsTrackingDisabled(location)) return

      const state = history.location.state as HistoryLocationState | null

      let promisedData: Promise<UncastObject>

      if (state) {
        promisedData = prepareTrackingData(state.userValues, state.steps)
      } else {
        promisedData = new Promise((resolve) => resolve({}))
      }

      const name = dash(currentStep.name.toLowerCase())

      promisedData.then((data) => {
        dispatchUserEvent(
          EMBEDDED_JOURNEY_MESSAGE_EVENT_TYPE.USER_EVENT_PAGE_VIEW,
          journey.id,
          {
            journeyName: journey.name,
            path: `epilot-journey/${journey.id}/step-${currentStep.orderIndex + 1}-${
              name
            }`,
            userValues: data ?? {}
          }
        )

        debug('[useAppEvents] Page view event sent')
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.id])
}
